import React, {createContext, useContext, useEffect, useState} from 'react';
import defaultConfig from './defaultConfig';
import PropTypes from 'prop-types';
import {useThemeActionsContext, useThemeContext} from './ThemeContextProvider';
import {LayoutDirection} from '../../../shared/constants/AppEnums';

const LocaleContext = createContext();
const LocaleActionsContext = createContext();

export const useLocaleContext = () => useContext(LocaleContext);
export const useLocaleActionsContext = () => useContext(LocaleActionsContext);

const STORAGE_KEY = 'app_locale';
const DIR_KEY = 'app_direction';

// Treat Arabic (and friends) as RTL. Add more codes as needed.
const RTL_LANGS = new Set(['ar', 'he', 'fa', 'ur', 'ps', 'ku', 'dv', 'ug']);
const isRtlLanguage = (code) => {
  const base = (code || '').toLowerCase().split(/[-_]/)[0];
  return RTL_LANGS.has(base);
};

const LocaleContextProvider = ({children}) => {
  // 1) Read persisted locale once at init
  const [locale, updateLocale] = useState(() => {
    try {
      const saved =
        typeof window !== 'undefined' && localStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved) : defaultConfig.locale;
    } catch (e) {
      return defaultConfig.locale;
    }
  });

  const {theme} = useThemeContext();
  const {updateTheme} = useThemeActionsContext();

  // 2) Persist on change + keep direction in sync + set <html> attrs
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(locale));
    } catch (e) {
      // ignore storage errors
    }

    const isRTL = isRtlLanguage(locale?.locale);

    // Update MUI theme direction if needed (avoid mutation)
    if (isRTL && theme.direction !== LayoutDirection.RTL) {
      updateTheme({...theme, direction: LayoutDirection.RTL});
    } else if (!isRTL && theme.direction !== LayoutDirection.LTR) {
      updateTheme({...theme, direction: LayoutDirection.LTR});
    }

    // Update document language + dir (helps with native rendering & SEO)
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('lang', locale?.locale || 'en');
      document.documentElement.setAttribute('dir', isRTL ? 'rtl' : 'ltr');
    }

    // Persist the direction so App/theme can bootstrap correctly on next load
    try {
      localStorage.setItem(
        DIR_KEY,
        isRTL ? LayoutDirection.RTL : LayoutDirection.LTR,
      );
    } catch (e) {
      // ignore
    }
  }, [locale, theme, updateTheme]);

  return (
    <LocaleContext.Provider
      value={{locale, rtlLocale: defaultConfig.rtlLocale}}
    >
      <LocaleActionsContext.Provider value={{updateLocale}}>
        {children}
      </LocaleActionsContext.Provider>
    </LocaleContext.Provider>
  );
};

export default LocaleContextProvider;

LocaleContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
